import { useEffect, useRef, useState } from 'react';

// Date picker custom (kalender bulanan), gaya Material date picker, tapi
// warnanya ikut var(--accent) punya web (bukan ungu kayak referensi).
// Konsisten sama TimePicker.jsx: animasi bounce buka/tutup pakai keyframe
// portfolio-modal-in/out yang sudah ada, tombol OK solid accent + rounded
// morph, Batal tetap teks polos.

const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTH_LABELS = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember',
];
const DAY_LABELS_LONG = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];

function pad2(n) {
  return String(n).padStart(2, '0');
}
function parseISO(value) {
  if (!value) return null;
  const [y, m, d] = value.split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
function toISO(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}
function isSameDay(a, b) {
  return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function formatHeader(date) {
  return `${DAY_LABELS_LONG[date.getDay()]}, ${date.getDate()} ${MONTH_LABELS[date.getMonth()].slice(0, 3)}`;
}

export default function DatePicker({ open, value, onClose, onSave }) {
  const today = new Date();
  const initialSelected = parseISO(value) || today;
  const [selected, setSelected] = useState(initialSelected);
  const [viewYear, setViewYear] = useState(initialSelected.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialSelected.getMonth());
  const [manual, setManual] = useState(false);
  const [manualText, setManualText] = useState('');
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [displayOpen, setDisplayOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const closeTimeoutRef = useRef(null);

  useEffect(() => {
    if (open) {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
      const init = parseISO(value) || today;
      setSelected(init);
      setViewYear(init.getFullYear());
      setViewMonth(init.getMonth());
      setManual(false);
      setMonthPickerOpen(false);
      setClosing(false);
      setDisplayOpen(true);
    } else if (displayOpen) {
      setClosing(true);
      closeTimeoutRef.current = setTimeout(() => {
        setDisplayOpen(false);
        setClosing(false);
      }, 280);
    }
    return () => { if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!displayOpen) return null;

  const firstOfMonth = new Date(viewYear, viewMonth, 1);
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const startWeekday = firstOfMonth.getDay();
  const cells = [
    ...Array.from({ length: startWeekday }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const goMonth = (delta) => {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setViewMonth(m);
    setViewYear(y);
  };

  const pickDay = (day) => {
    setSelected(new Date(viewYear, viewMonth, day));
  };

  const applyManual = () => {
    const d = parseISO(manualText.replaceAll('/', '-'));
    if (d) {
      setSelected(d);
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
    }
    setManual(false);
  };

  const save = () => onSave(toISO(selected));

  const yearRange = Array.from({ length: 12 }, (_, i) => viewYear - 6 + i);

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm transition-opacity duration-[280ms] ${closing ? 'opacity-0' : 'opacity-100'}`}
      onClick={onClose}
    >
      <div
        className={`w-full max-w-[360px] rounded-3xl bg-white dark:bg-neutral-800 shadow-2xl overflow-hidden ${closing ? 'portfolio-modal-out' : 'portfolio-modal-in'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-4">
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">Pilih Tanggal</p>

          {!manual ? (
            <div className="flex items-center justify-between gap-3">
              <p className="text-3xl font-extrabold text-neutral-800 dark:text-white leading-tight truncate">{formatHeader(selected)}</p>
              <button
                type="button"
                onClick={() => { setManual(true); setManualText(toISO(selected)); }}
                className="w-10 h-10 rounded-full flex items-center justify-center text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors flex-shrink-0"
                title="Ketik manual"
              >
                <span className="material-symbols-outlined text-xl">edit</span>
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={manualText}
                onChange={(e) => setManualText(e.target.value)}
                className="flex-1 h-14 px-3 rounded-2xl text-lg font-bold border border-neutral-200 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-900/50 dark:text-white outline-none focus:ring-2 focus:ring-[var(--accent)]"
              />
              <button
                type="button"
                onClick={applyManual}
                className="w-10 h-10 rounded-full flex items-center justify-center text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors flex-shrink-0"
                title="Kembali ke kalender"
              >
                <span className="material-symbols-outlined text-xl">calendar_month</span>
              </button>
            </div>
          )}
        </div>

        <div className="border-t border-neutral-200 dark:border-neutral-700" />

        <div className="px-4 pt-4 pb-2">
          <div className="flex items-center justify-between mb-3 px-2">
            <button
              type="button"
              onClick={() => setMonthPickerOpen((v) => !v)}
              className="flex items-center gap-1 text-sm font-extrabold text-neutral-800 dark:text-white px-2 py-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
            >
              {MONTH_LABELS[viewMonth]} {viewYear}
              <span className="material-symbols-outlined text-lg transition-transform" style={{ transform: monthPickerOpen ? 'rotate(180deg)' : 'none' }}>arrow_drop_down</span>
            </button>
            {!monthPickerOpen && (
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => goMonth(-1)} className="w-8 h-8 rounded-full flex items-center justify-center text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors">
                  <span className="material-symbols-outlined text-lg">chevron_left</span>
                </button>
                <button type="button" onClick={() => goMonth(1)} className="w-8 h-8 rounded-full flex items-center justify-center text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors">
                  <span className="material-symbols-outlined text-lg">chevron_right</span>
                </button>
              </div>
            )}
          </div>

          {monthPickerOpen ? (
            <div className="grid grid-cols-3 gap-2 px-2 py-2 max-h-[260px] overflow-y-auto">
              {yearRange.map((y) => (
                <button
                  key={y}
                  type="button"
                  onClick={() => { setViewYear(y); setMonthPickerOpen(false); }}
                  className="h-11 rounded-xl text-sm font-bold transition-colors"
                  style={y === viewYear
                    ? { backgroundColor: 'var(--accent)', color: '#fff' }
                    : { color: '#525252' }}
                >
                  {y}
                </button>
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-7 gap-y-1 px-2 mb-1">
                {DAY_LABELS.map((d, i) => (
                  <div key={i} className="h-8 flex items-center justify-center text-xs font-bold text-neutral-500 dark:text-neutral-400">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-y-1 px-2 pb-2">
                {cells.map((day, i) => {
                  if (!day) return <div key={i} className="h-10" />;
                  const cellDate = new Date(viewYear, viewMonth, day);
                  const isToday = isSameDay(cellDate, today);
                  const isSelected = isSameDay(cellDate, selected);
                  return (
                    <div key={i} className="h-10 flex items-center justify-center">
                      <button
                        type="button"
                        onClick={() => pickDay(day)}
                        className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-150"
                        style={
                          isSelected
                            ? { backgroundColor: 'var(--accent)', color: '#fff', fontWeight: 700 }
                            : isToday
                              ? { border: '1.5px solid var(--accent)', color: 'var(--accent)' }
                              : { color: '#404040' }
                        }
                      >
                        {day}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-1 px-4 py-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm font-extrabold hover:bg-[color-mix(in_oklab,var(--accent)_10%,transparent)] transition-colors"
            style={{ color: 'var(--accent)' }}
          >
            Batal
          </button>
          <button
            type="button"
            onClick={save}
            className="px-5 py-2.5 rounded-[20px] text-sm font-extrabold text-white shadow-sm hover:shadow-md hover:rounded-[14px] active:rounded-[10px] active:scale-95 transition-all duration-300 ease-[var(--ease-spring)]"
            style={{ backgroundColor: '#4A7A3A' }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}
